/**
 * FAQ Help Article - Frequently Asked Questions
 * Renders comprehensive FAQ content from markdown source
 */

import type { LoaderFunctionArgs } from 'react-router';
import { useLoaderData, useSearchParams, Link, useNavigation } from 'react-router';
import {
  Page,
  BlockStack,
  Card,
  Text,
  InlineStack,
  Icon,
  Box,
  Button,
  SkeletonPage,
  SkeletonBodyText,
  SkeletonDisplayText,
} from '@shopify/polaris';
import { ClockIcon } from '@shopify/polaris-icons';
import type { Env } from '../../src/types';
import { useLoading } from '../hooks/shopify';
import { getArticleContent } from '../content/help-articles';
import { getEnv } from '~/utils/cloudflare-context';

export async function loader({ context, request }: LoaderFunctionArgs) {
  const env = getEnv(context);
  const url = new URL(request.url);
  const shop = url.searchParams.get('shop');

  // Track article view
  if (shop) {
    try {
      const shopResult = await env.DB.prepare('SELECT id FROM shops WHERE shop_domain = ?').bind(shop).first();

      if (shopResult) {
        await env.DB.prepare(
          `INSERT INTO help_center_views (shop_id, article_slug, article_title, session_id)
           VALUES (?, ?, ?, ?)`
        )
          .bind(shopResult.id, 'faq', 'Frequently Asked Questions', 'session-' + Date.now())
          .run();
      }
    } catch (error) {
      // Article view tracking failed - non-critical analytics, silent fail
    }
  }

  // Load FAQ content from markdown
  let content = getArticleContent('faq') || '';

  // Handle Vite import formatting
  if (content.startsWith('export default')) {
    const match = content.match(/export default ["'](.+)["'];?$/s);
    if (match) {
      content = match[1]
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'");
    }
  }

  return {
    article: {
      title: 'Frequently Asked Questions',
      category: 'troubleshooting',
      readTime: 10,
      lastUpdated: '2025-01-03',
      content,
    },
  };
}

export default function FAQArticle() {
  const { article } = useLoaderData<typeof loader>();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const queryString = searchParams.toString();

  // Page loading state
  const isPageLoading = navigation.state === 'loading';

  // Control App Bridge loading indicator
  useLoading(isPageLoading);

  // Show skeleton page while loading
  if (isPageLoading) {
    return (
      <SkeletonPage
        title="Frequently Asked Questions"
        backAction
      >
        <BlockStack gap="500">
          <Card>
            <SkeletonBodyText lines={2} />
          </Card>
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={6} />
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={5} />
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={4} />
            </BlockStack>
          </Card>
        </BlockStack>
      </SkeletonPage>
    );
  }

  // Render markdown content
  const renderMarkdownContent = (content: string) => {
    return content.split('\n\n').map((block: string, index: number) => {
      const trimmedBlock = block.trim();
      if (!trimmedBlock) return null;

      // Skip the H1 title (we show it in Page component)
      if (trimmedBlock.startsWith('# ')) {
        return null;
      }

      // H2 Heading
      if (trimmedBlock.startsWith('## ')) {
        return (
          <Box key={index} paddingBlockStart="400" paddingBlockEnd="200">
            <Text as="h2" variant="headingLg">
              {trimmedBlock.replace('## ', '')}
            </Text>
          </Box>
        );
      }

      // H3 Heading
      if (trimmedBlock.startsWith('### ')) {
        return (
          <Box key={index} paddingBlockStart="300" paddingBlockEnd="100">
            <Text as="h3" variant="headingMd">
              {trimmedBlock.replace('### ', '')}
            </Text>
          </Box>
        );
      }

      // Code blocks
      if (trimmedBlock.startsWith('```')) {
        const lines = trimmedBlock.split('\n');
        const codeContent = lines.slice(1, -1).join('\n');
        return (
          <Box
            key={index}
            padding="400"
            background="bg-surface-secondary"
            borderRadius="200"
          >
            <pre
              style={{
                margin: 0,
                fontFamily: 'Monaco, Menlo, Consolas, monospace',
                fontSize: '13px',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowX: 'auto',
              }}
            >
              <Text as="span" variant="bodySm">
                {codeContent}
              </Text>
            </pre>
          </Box>
        );
      }

      // Tables
      if (trimmedBlock.includes('|') && trimmedBlock.split('\n').length > 2) {
        const rows = trimmedBlock.split('\n').filter(row => row.trim());
        const hasHeader = rows.length > 1 && rows[1].includes('---');

        if (hasHeader) {
          const headers = rows[0].split('|').filter(cell => cell.trim()).map(cell => cell.trim());
          const dataRows = rows.slice(2).map(row =>
            row.split('|').filter(cell => cell.trim()).map(cell => cell.trim())
          );

          return (
            <div key={index} style={{ overflowX: 'auto' }}>
              <Box borderWidth="025" borderColor="border" borderRadius="200" padding="0">
                {/* Table header */}
                <Box padding="300" background="bg-surface-secondary">
                  <InlineStack gap="400" wrap={false}>
                    {headers.map((header, i) => (
                      <Box key={i} minWidth="120px">
                        <Text as="span" variant="bodyMd" fontWeight="medium">
                          {header}
                        </Text>
                      </Box>
                    ))}
                  </InlineStack>
                </Box>
                {/* Table rows */}
                <Box padding="300">
                  <BlockStack gap="200">
                    {dataRows.map((row, rowIndex) => (
                      <InlineStack key={rowIndex} gap="400" wrap={false}>
                        {row.map((cell, cellIndex) => (
                          <Box key={cellIndex} minWidth="120px">
                            <Text as="span" variant="bodySm">
                              {cell}
                            </Text>
                          </Box>
                        ))}
                      </InlineStack>
                    ))}
                  </BlockStack>
                </Box>
              </Box>
            </div>
          );
        }
      }

      // Bullet lists
      if (trimmedBlock.includes('\n- ') || trimmedBlock.startsWith('- ')) {
        const items = trimmedBlock.split('\n').filter((line: string) => line.trim().startsWith('- '));
        return (
          <Box key={index}>
            <BlockStack gap="200">
              {items.map((item: string, i: number) => {
                const text = item.replace(/^- /, '').trim();
                const isBold = text.startsWith('**') && text.includes('**:');

                if (isBold) {
                  const [boldPart, ...rest] = text.split('**:');
                  const label = boldPart.replace('**', '');
                  const description = rest.join('**:').trim();

                  return (
                    <InlineStack key={i} gap="200" blockAlign="start">
                      <Text as="span" variant="bodyMd" tone="subdued">-</Text>
                      <Box>
                        <Text as="span" variant="bodyMd" fontWeight="medium">
                          {label}:
                        </Text>
                        {' '}
                        <Text as="span" variant="bodyMd" tone="subdued">
                          {description}
                        </Text>
                      </Box>
                    </InlineStack>
                  );
                }

                // Handle [Supported], [Coming Soon], etc. labels
                const labelMatch = text.match(/^\[([^\]]+)\] (.+)/);
                if (labelMatch) {
                  return (
                    <InlineStack key={i} gap="200" blockAlign="start">
                      <Text as="span" variant="bodyMd" tone="subdued">-</Text>
                      <Box>
                        <Text as="span" variant="bodySm" fontWeight="medium" tone="subdued">
                          [{labelMatch[1]}]
                        </Text>
                        {' '}
                        <Text as="span" variant="bodyMd" tone="subdued">
                          {labelMatch[2]}
                        </Text>
                      </Box>
                    </InlineStack>
                  );
                }

                return (
                  <InlineStack key={i} gap="200" blockAlign="start">
                    <Text as="span" variant="bodyMd" tone="subdued">-</Text>
                    <Text as="span" variant="bodyMd" tone="subdued">
                      {text}
                    </Text>
                  </InlineStack>
                );
              })}
            </BlockStack>
          </Box>
        );
      }

      // Numbered lists
      if (/^\d+\. /.test(trimmedBlock) || trimmedBlock.includes('\n1. ')) {
        const items = trimmedBlock.split('\n').filter((line: string) => /^\d+\. /.test(line.trim()));
        return (
          <Box key={index}>
            <BlockStack gap="200">
              {items.map((item: string, i: number) => {
                const text = item.replace(/^\d+\. /, '').trim();
                return (
                  <InlineStack key={i} gap="200" blockAlign="start">
                    <Text as="span" variant="bodyMd" tone="subdued">{i + 1}.</Text>
                    <Text as="span" variant="bodyMd" tone="subdued">
                      {text}
                    </Text>
                  </InlineStack>
                );
              })}
            </BlockStack>
          </Box>
        );
      }

      // Horizontal rules
      if (trimmedBlock === '---') {
        return (
          <Box key={index} paddingBlock="300">
            <div style={{ height: '1px', backgroundColor: '#e1e3e5' }} />
          </Box>
        );
      }

      // Q&A format (lines starting with **Q:** or **A:**)
      if (trimmedBlock.startsWith('**Q:')) {
        const lines = trimmedBlock.split('\n');
        const question = lines[0].replace('**Q:', '').replace('**', '').trim();
        const answer = lines.slice(1).join(' ').replace('A:', '').trim();

        return (
          <Box key={index} paddingBlockEnd="200">
            <BlockStack gap="100">
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                Q: {question}
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                A: {answer}
              </Text>
            </BlockStack>
          </Box>
        );
      }

      // Regular paragraphs with bold and link support
      const hasBold = trimmedBlock.includes('**');
      if (hasBold) {
        const parts = trimmedBlock.split('**');
        return (
          <Box key={index}>
            <Text as="p" variant="bodyMd" tone="subdued">
              {parts.map((part, i) =>
                i % 2 === 0 ? (
                  <span key={i}>{part}</span>
                ) : (
                  <Text key={i} as="span" variant="bodyMd" fontWeight="medium">
                    {part}
                  </Text>
                )
              )}
            </Text>
          </Box>
        );
      }

      // Regular paragraph
      return (
        <Box key={index}>
          <Text as="p" variant="bodyMd" tone="subdued">
            {trimmedBlock}
          </Text>
        </Box>
      );
    });
  };

  return (
    <div
      className="page-enter"
      style={{
        backgroundColor: '#f4f4f6',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      <div style={{ position: 'relative', zIndex: 1 }}>
        <Page
          title={article.title}
          subtitle="Quick answers to common questions"
          backAction={{
            content: 'Help Center',
            url: `/app/help${queryString ? `?${queryString}` : ''}`,
          }}
        >
          <BlockStack gap="500">
            {/* Article Info */}
            <div className="slide-in-up">
              <Card>
                <InlineStack gap="400" blockAlign="center">
                  <InlineStack gap="200" blockAlign="center">
                    <Icon source={ClockIcon} tone="subdued" />
                    <Text as="span" variant="bodySm" tone="subdued">
                      {article.readTime} min read
                    </Text>
                  </InlineStack>
                  <Text as="span" variant="bodySm" tone="subdued">
                    Last updated: {new Date(article.lastUpdated).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Text>
                </InlineStack>
              </Card>
            </div>

            {/* FAQ Content from Markdown */}
            <div className="slide-in-up" style={{ animationDelay: '50ms' }}>
              <Card>
                <BlockStack gap="300">
                  {renderMarkdownContent(article.content)}
                </BlockStack>
              </Card>
            </div>

            {/* Still Have Questions */}
            <div className="slide-in-up" style={{ animationDelay: '100ms' }}>
              <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd" fontWeight="semibold">
                    Still have questions?
                  </Text>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Our support team is here to help with any questions about Stock Sentry.
                  </Text>
                  <InlineStack align="start">
                    <Link to={`/app/support${queryString ? `?${queryString}` : ''}`}>
                      <Button variant="primary">Contact Support</Button>
                    </Link>
                  </InlineStack>
                </BlockStack>
              </Card>
            </div>
          </BlockStack>
        </Page>
      </div>
    </div>
  );
}
